(() => {
  const config = window.MerachAutoModuleTrackingConfig || {};

  if (
    !config.enabled
    || (config.isPreview && config.trackPreview === false)
    || window.MerachAutoModuleTrackingLoaded
  ) {
    return;
  }

  window.MerachAutoModuleTrackingLoaded = true;

  const EVENT_NAME = 'merach_module_interaction';
  const SCHEMA_VERSION = '3.1';
  const SECTION_PREFIX = 'shopify-section-';
  const SECTION_SELECTOR = `[id^="${SECTION_PREFIX}"]`;
  const VIEW_DELAY_MS = Number(config.viewDelayMs) || 800;
  const MAX_CONTENT_LENGTH = Number(config.maxContentLength) || 360;
  const INTERACTIVE_SELECTOR = [
    'a[href]',
    'button',
    'summary',
    '[role="button"]',
    '[onclick]',
    'input[type="button"]',
    'input[type="submit"]',
    'input[type="checkbox"]',
    'input[type="radio"]',
    'select'
  ].join(',');
  const ATTRIBUTION_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'gclid',
    'fbclid',
    'msclkid',
    'ttclid'
  ];

  /*
   * These types contain the store's older inline tracking implementation.
   * When an old instance-level marker is present, that implementation remains
   * authoritative to avoid duplicate events. If its old switch is off, this
   * global tracker automatically fills the gap.
   */
  const legacyTrackedTypes = new Set([
    'bricks-comments',
    'collection-list-custom',
    'collection-tab',
    'compose-marquee',
    'custom-grid',
    'custom-media-grid',
    'faq',
    'featured-slide',
    'float-image-block',
    'image-text-sroll',
    'link-tab-check',
    'media-tab',
    'minimum-spend',
    'multi-column-pro',
    'press',
    'related-products',
    'rich-text',
    'sale-grid',
    'slide-banner',
    'slideshow',
    'text-with-icons',
    'video',
    'wabeez-custom-icon-grid'
  ]);

  const excludedTypes = new Set(
    String(config.excludedSectionTypes || '')
      .split(/[\n,]/)
      .map((value) => normalizeType(value))
      .filter(Boolean)
  );

  const registeredSections = new WeakSet();
  const viewedSections = new WeakSet();
  const legacyOwnedSections = new WeakSet();
  const observedTargets = new WeakMap();
  const visibleTimers = new WeakMap();
  const visibilityStates = new WeakMap();
  const durationTrackedSections = new Set();
  const recentClicks = new WeakMap();
  const attribution = buildAttribution();
  const navigationContext = buildNavigationContext();
  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver(handleIntersections, {
        threshold: [0, 0.05, 0.2, 0.35, 0.5]
      })
    : null;

  function normalizeType(value) {
    return String(value || '')
      .trim()
      .replace(/_/g, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();
  }

  function firstValue(...values) {
    return values.find((value) => {
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }

      return value !== undefined && value !== null;
    });
  }

  function cleanText(value, maxLength = 120) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maxLength);
  }

  function humanize(value) {
    return String(value || '')
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  function safeStorage(storage, operation, key, value) {
    try {
      if (operation === 'get') {
        return storage.getItem(key);
      }

      storage.setItem(key, value);
      return value;
    } catch (error) {
      return null;
    }
  }

  function parseStoredJson(storage, key) {
    const stored = safeStorage(storage, 'get', key);

    if (!stored) {
      return {};
    }

    try {
      return JSON.parse(stored);
    } catch (error) {
      return {};
    }
  }

  function removeStoredValue(storage, key) {
    try {
      storage.removeItem(key);
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsers.
    }
  }

  function buildNavigationContext() {
    const key = 'merach_module_navigation_v3';
    const previous = parseStoredJson(window.sessionStorage, key);

    if (!previous.timestamp) {
      return {};
    }

    const age = Date.now() - Number(previous.timestamp);
    const destinationPath = String(previous.destination_path || '').split('#')[0];
    const currentPath = `${window.location.pathname}${window.location.search}`;
    const matchesCurrentPage = destinationPath === currentPath
      || destinationPath === window.location.pathname;

    if (age < 0 || age > 30 * 60 * 1000 || !matchesCurrentPage) {
      if (age > 30 * 60 * 1000) {
        removeStoredValue(window.sessionStorage, key);
      }
      return {};
    }

    removeStoredValue(window.sessionStorage, key);
    return {
      navigation_from_module: true,
      previous_page_path: previous.page_path || '',
      previous_page_type: previous.page_type || '',
      previous_section_type: previous.section_type || '',
      previous_section_title: previous.section_title || '',
      previous_section_position: previous.section_position || null,
      previous_click_text: previous.click_text || '',
      previous_destination_path: previous.destination_path || '',
      previous_click_timestamp: previous.clicked_at || ''
    };
  }

  function rememberModuleNavigation(payload) {
    if (!payload.destination_path || !payload.destination_url) {
      return;
    }

    try {
      const destination = new URL(payload.destination_url, window.location.origin);

      if (destination.origin !== window.location.origin) {
        return;
      }
    } catch (error) {
      return;
    }

    safeStorage(window.sessionStorage, 'set', 'merach_module_navigation_v3', JSON.stringify({
      timestamp: Date.now(),
      clicked_at: new Date().toISOString(),
      page_path: payload.page_path,
      page_type: payload.page_type,
      section_type: payload.section_type,
      section_title: payload.section_title,
      section_position: payload.section_position,
      click_text: payload.click_text,
      destination_path: payload.destination_path,
      posthog_session_id: payload.posthog_session_id
    }));
  }

  function externalReferrer() {
    if (!document.referrer) {
      return '';
    }

    try {
      const referrer = new URL(document.referrer);
      return referrer.origin === window.location.origin ? '' : referrer.href;
    } catch (error) {
      return '';
    }
  }

  function referrerSource(referrer) {
    if (!referrer) {
      return '';
    }

    try {
      return new URL(referrer).hostname.replace(/^www\./, '');
    } catch (error) {
      return '';
    }
  }

  function classifyChannel(values) {
    const source = String(values.utm_source || values.referrer_source || '').toLowerCase();
    const medium = String(values.utm_medium || '').toLowerCase();

    if (values.gclid || values.msclkid || /(cpc|ppc|paid|sem|display)/.test(medium)) {
      return 'Paid Search';
    }

    if (values.fbclid || /(facebook|instagram|meta)/.test(source)) {
      return medium.includes('paid') ? 'Paid Social' : 'Organic Social';
    }

    if (values.ttclid || source.includes('tiktok')) {
      return medium.includes('paid') ? 'Paid Social' : 'Organic Social';
    }

    if (/(email|newsletter|edm)/.test(medium) || source.includes('klaviyo')) {
      return 'Email';
    }

    if (/(affiliate|partner)/.test(medium)) {
      return 'Affiliate';
    }

    if (/(social|social-network|social-media)/.test(medium)) {
      return 'Organic Social';
    }

    if (/(google|bing|yahoo|duckduckgo)/.test(source)) {
      return 'Organic Search';
    }

    if (values.referrer_source) {
      return 'Referral';
    }

    return 'Direct';
  }

  function currentAttribution() {
    const params = new URLSearchParams(window.location.search);
    const values = {};

    ATTRIBUTION_KEYS.forEach((key) => {
      const value = params.get(key);

      if (value) {
        values[key] = cleanText(value, 240);
      }
    });

    values.landing_page = `${window.location.pathname}${window.location.search}`;
    values.referrer = externalReferrer();
    values.referrer_source = referrerSource(values.referrer);
    values.traffic_channel = classifyChannel(values);
    values.attribution_timestamp = new Date().toISOString();

    return values;
  }

  function hasCampaignSignal(values) {
    return ATTRIBUTION_KEYS.some((key) => Boolean(values[key])) || Boolean(values.referrer);
  }

  function buildAttribution() {
    const current = currentAttribution();
    const sessionKey = 'merach_session_attribution_v2';
    const firstKey = 'merach_first_attribution_v2';
    const storedSession = parseStoredJson(window.sessionStorage, sessionKey);
    const session = hasCampaignSignal(current) || !Object.keys(storedSession).length
      ? current
      : storedSession;

    safeStorage(window.sessionStorage, 'set', sessionKey, JSON.stringify(session));

    const storedFirst = parseStoredJson(window.localStorage, firstKey);
    const first = Object.keys(storedFirst).length ? storedFirst : current;

    if (!Object.keys(storedFirst).length) {
      safeStorage(window.localStorage, 'set', firstKey, JSON.stringify(first));
    }

    const properties = {
      traffic_channel: session.traffic_channel || 'Direct',
      traffic_source: session.utm_source || session.referrer_source || '(direct)',
      traffic_medium: session.utm_medium || '(none)',
      traffic_campaign: session.utm_campaign || '',
      traffic_content: session.utm_content || '',
      traffic_term: session.utm_term || '',
      landing_page: session.landing_page || window.location.pathname,
      initial_referrer: session.referrer || '',
      gclid: session.gclid || '',
      fbclid: session.fbclid || '',
      msclkid: session.msclkid || '',
      ttclid: session.ttclid || '',
      first_traffic_channel: first.traffic_channel || 'Direct',
      first_traffic_source: first.utm_source || first.referrer_source || '(direct)',
      first_traffic_medium: first.utm_medium || '(none)',
      first_traffic_campaign: first.utm_campaign || '',
      first_landing_page: first.landing_page || ''
    };

    registerSessionProperties(properties);
    return properties;
  }

  function registerSessionProperties(properties) {
    let attempts = 0;
    const register = () => {
      attempts += 1;

      if (window.posthog && typeof window.posthog.register_for_session === 'function') {
        window.posthog.register_for_session(properties);
        return;
      }

      if (attempts < 20) {
        window.setTimeout(register, 250);
      }
    };

    register();
  }

  function sectionId(wrapper) {
    return wrapper.id.startsWith(SECTION_PREFIX)
      ? wrapper.id.slice(SECTION_PREFIX.length)
      : wrapper.id;
  }

  function sectionTypeFromClass(wrapper) {
    const className = Array.from(wrapper.classList).find((name) => name.startsWith('shopify-section--'));
    return normalizeType(className ? className.slice('shopify-section--'.length) : '');
  }

  function sectionTypeFromId(wrapper) {
    const id = sectionId(wrapper);
    const separatorIndex = id.lastIndexOf('__');

    if (separatorIndex === -1) {
      return '';
    }

    let value = id.slice(separatorIndex + 2);
    const finalToken = value.split('_').pop() || '';

    // Shopify appends a generated instance token to JSON-template sections.
    // Tokens vary in length (for example JPtr4 or 7wBh9N), so an exact
    // six-character suffix check fragments otherwise identical section types.
    if (
      /^[a-zA-Z0-9]{4,12}$/.test(finalToken)
      && /[A-Z0-9]/.test(finalToken)
      && value.includes('_')
    ) {
      value = value.slice(0, -(finalToken.length + 1));
    }

    if (/^[a-f0-9]{12,}$/i.test(value)) {
      return '';
    }

    return normalizeType(value);
  }

  function sectionType(wrapper) {
    const idType = sectionTypeFromId(wrapper);
    const classType = sectionTypeFromClass(wrapper);

    if (!idType || idType === 'main') {
      return classType || idType || 'unknown-section';
    }

    return idType;
  }

  function isExcluded(wrapper, type = sectionType(wrapper)) {
    if (excludedTypes.has(type)) {
      return true;
    }

    return Array.from(wrapper.classList).some((className) => {
      if (!className.startsWith('shopify-section--')) {
        return false;
      }

      return excludedTypes.has(normalizeType(className.slice('shopify-section--'.length)));
    });
  }

  function trackableSections() {
    return Array.from(document.querySelectorAll(SECTION_SELECTOR)).filter(
      (wrapper) => !isExcluded(wrapper)
    );
  }

  function sectionPosition(wrapper) {
    const sections = trackableSections();
    const index = sections.indexOf(wrapper);
    return {
      position: index >= 0 ? index + 1 : null,
      total: sections.length
    };
  }

  function pageArea(wrapper) {
    if (
      wrapper.closest('header')
      || wrapper.closest('[id^="shopify-section-sections--"][id*="__header"]')
      || wrapper.id.includes('__header')
    ) {
      return 'header';
    }

    if (
      wrapper.closest('footer')
      || wrapper.closest('[id^="shopify-section-sections--"][id*="__footer"]')
      || wrapper.id.includes('__footer')
    ) {
      return 'footer';
    }

    return 'main';
  }

  function areaPosition(wrapper) {
    const area = pageArea(wrapper);
    const sections = trackableSections().filter((section) => pageArea(section) === area);
    const index = sections.indexOf(wrapper);

    return {
      area,
      position: index >= 0 ? index + 1 : null,
      total: sections.length
    };
  }

  function markerFor(wrapper) {
    return wrapper.querySelector('[data-mrk-track-config],[data-mrk-track-section],[data-mrk-track-item]');
  }

  function isLegacyOwned(wrapper, type, marker = markerFor(wrapper)) {
    if (!legacyTrackedTypes.has(type) || !marker) {
      return false;
    }

    return !marker.matches('[data-mrk-track-config]')
      && marker.matches('[data-mrk-track-section],[data-mrk-track-item]');
  }

  function moduleTitle(wrapper, marker, type) {
    const configuredTitle = marker?.dataset.mrkTrackModuleTitle;

    if (configuredTitle) {
      return cleanText(configuredTitle);
    }

    const heading = wrapper.querySelector('h1,h2,h3,h4,[role="heading"]');
    return cleanText(heading?.textContent) || humanize(type);
  }

  function sectionContent(wrapper, title) {
    const values = [];

    wrapper.querySelectorAll('h1,h2,h3,h4,[role="heading"],p').forEach((element) => {
      if (values.length >= 4 || element.closest('form,[hidden],[aria-hidden="true"]')) {
        return;
      }

      const text = cleanText(element.textContent, 160);

      if (text && text !== title && !values.includes(text)) {
        values.push(text);
      }
    });

    return cleanText([title, ...values].filter(Boolean).join(' | '), MAX_CONTENT_LENGTH);
  }

  function contentSignature(value) {
    let hash = 2166136261;
    const normalized = cleanText(value, MAX_CONTENT_LENGTH).toLowerCase();

    for (let index = 0; index < normalized.length; index += 1) {
      hash ^= normalized.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }

    return `fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`;
  }

  function trackingContext() {
    return window.MerachTrackingContext || {};
  }

  function canonicalPageType() {
    const shopifyPageType = String(config.shopifyPageType || '')
      .trim()
      .replace(/-/g, '_')
      .toLowerCase();
    const pageTypeMap = {
      index: 'HOME',
      product: 'PDP',
      collection: 'COLLECTION',
      page: 'PAGE',
      blog: 'BLOG',
      article: 'ARTICLE',
      cart: 'CART',
      search: 'SEARCH',
      password: 'PASSWORD',
      gift_card: 'GIFT_CARD',
      customers_account: 'ACCOUNT',
      customers_addresses: 'ACCOUNT',
      customers_login: 'ACCOUNT',
      customers_order: 'ACCOUNT',
      customers_register: 'ACCOUNT',
      customers_reset_password: 'ACCOUNT',
      customers_activate_account: 'ACCOUNT'
    };

    if (pageTypeMap[shopifyPageType]) {
      return pageTypeMap[shopifyPageType];
    }

    return String(config.shopifyPageType || '').trim().toUpperCase() || 'PAGE';
  }

  function canonicalPageTypeLabel(pageType) {
    const labels = {
      HOME: 'Home Page',
      PDP: 'Product Detail Page',
      COLLECTION: 'Collection Page',
      PAGE: 'Custom Page',
      BLOG: 'Blog Page',
      ARTICLE: 'Article Page',
      CART: 'Cart Page',
      SEARCH: 'Search Page',
      ACCOUNT: 'Customer Account',
      PASSWORD: 'Password Page',
      GIFT_CARD: 'Gift Card Page'
    };

    return labels[pageType] || humanize(pageType);
  }

  function canonicalTemplate() {
    const name = String(config.shopifyTemplate || '').trim();
    const suffix = String(config.shopifyTemplateSuffix || '').trim();
    return suffix ? `${name}.${suffix}` : name;
  }

  function posthogValue(methodName) {
    try {
      const value = window.posthog?.[methodName]?.();
      return typeof value === 'string' ? value : '';
    } catch (error) {
      return '';
    }
  }

  function linkDestination(element) {
    const anchor = element?.closest?.('a[href]');

    if (!anchor?.href) {
      return {
        url: '',
        path: ''
      };
    }

    try {
      const url = new URL(anchor.href, window.location.origin);
      return {
        url: url.href,
        path: url.origin === window.location.origin
          ? `${url.pathname}${url.search}${url.hash}`
          : url.origin
      };
    } catch (error) {
      return {
        url: '',
        path: ''
      };
    }
  }

  function interactivePosition(wrapper, clickedElement) {
    if (!clickedElement) {
      return null;
    }

    const elements = Array.from(wrapper.querySelectorAll(INTERACTIVE_SELECTOR));
    const index = elements.indexOf(clickedElement);
    return index >= 0 ? index + 1 : null;
  }

  function buildPayload(wrapper, target, action, clickedElementOverride, extras = {}) {
    const pageContext = trackingContext();
    const marker = markerFor(wrapper);
    const type = sectionType(wrapper);
    const order = sectionPosition(wrapper);
    const areaOrder = areaPosition(wrapper);
    /*
     * request.page_type comes from Shopify's top-level Liquid request and is
     * authoritative. Section-level legacy snippets may still mutate
     * MerachTrackingContext, but they must never redefine the current page.
     */
    const pageType = canonicalPageType();
    const templateName = canonicalTemplate();
    const pageKey = firstValue(
      config.shopifyPageKey,
      window.location.pathname
    );
    const moduleName = firstValue(
      target?.dataset?.mrkTrackModuleName,
      marker?.dataset?.mrkTrackModuleName,
      type
    );
    const title = moduleTitle(wrapper, marker, type);
    const content = sectionContent(wrapper, title);
    const contentKey = firstValue(
      target?.dataset?.mrkTrackContentKey,
      marker?.dataset?.mrkTrackContentKey,
      pageKey
    );
    const contentSku = firstValue(
      target?.dataset?.mrkTrackContentSku,
      marker?.dataset?.mrkTrackContentSku
    );
    const clickedElement = action === 'click'
      ? clickedElementOverride || target?.closest?.(INTERACTIVE_SELECTOR) || target
      : null;
    const destination = linkDestination(clickedElement);

    const payload = {
      ...pageContext,
      ...attribution,
      ...navigationContext,
      ...extras,
      schema_version: SCHEMA_VERSION,
      action,
      tracking_name: [pageType, pageKey, moduleName, contentKey].filter(Boolean).join('_'),
      page_type: pageType,
      page_type_label: canonicalPageTypeLabel(pageType),
      page_type_source: 'shopify_request',
      page_key: pageKey,
      page_sku: firstValue(config.shopifyPageSku, pageType === 'PDP' ? pageKey : null),
      page_handle: firstValue(config.shopifyPageHandle, window.location.pathname),
      page_url: window.location.href,
      page_path: `${window.location.pathname}${window.location.search}`,
      page_title: cleanText(document.title, 180),
      module_name: moduleName,
      module_label: firstValue(
        target?.dataset?.mrkTrackModuleLabel,
        marker?.dataset?.mrkTrackModuleLabel,
        humanize(moduleName)
      ),
      module_source: 'automatic',
      module_title: title,
      section_id: sectionId(wrapper),
      section_instance_id: sectionId(wrapper),
      section_type: type,
      section_title: title,
      section_position: order.position,
      section_total: order.total,
      page_area: areaOrder.area,
      section_position_in_area: areaOrder.position,
      section_total_in_area: areaOrder.total,
      section_content: content,
      content_signature: contentSignature([type, title, content, contentKey].join('|')),
      content_key: contentKey,
      content_sku: contentSku || null,
      content_type: target?.dataset?.mrkTrackContentType || (action === 'click' ? 'interaction' : 'module'),
      content_handle: target?.dataset?.mrkTrackContentHandle || null,
      item_position: Number(target?.dataset?.mrkTrackItemPosition || 0) || null,
      template: firstValue(templateName, document.body.dataset.template, pageContext.template),
      theme_id: String(config.shopifyThemeId || ''),
      theme_name: config.shopifyThemeName || '',
      theme_role: config.shopifyThemeRole || '',
      tracking_environment: config.isPreview ? 'preview' : 'production',
      is_preview: Boolean(config.isPreview),
      is_design_mode: Boolean(config.isDesignMode),
      posthog_session_id: posthogValue('get_session_id'),
      posthog_distinct_id: posthogValue('get_distinct_id'),
      auto_tracking: true
    };

    if (action === 'click') {
      payload.action_sku = firstValue(target?.dataset?.mrkTrackActionSku, contentSku) || null;
      payload.click_element = clickedElement?.tagName?.toLowerCase() || '';
      payload.click_text = cleanText(
        firstValue(
          clickedElement?.getAttribute?.('aria-label'),
          clickedElement?.getAttribute?.('title'),
          clickedElement?.textContent
        )
      );
      payload.click_position = interactivePosition(wrapper, clickedElement);
      payload.destination_url = destination.url;
      payload.destination_path = destination.path;
      payload.click_path = destination.path;
    }

    return payload;
  }

  function isRapidDuplicateClick(element, action) {
    const now = Date.now();
    const previous = recentClicks.get(element);
    recentClicks.set(element, {
      action,
      timestamp: now
    });
    return previous?.action === action && now - previous.timestamp < 500;
  }

  function capture(wrapper, target, action, clickedElement, extras) {
    if (!window.posthog || typeof window.posthog.capture !== 'function') {
      return;
    }

    if (action === 'view') {
      if (viewedSections.has(wrapper)) {
        return;
      }
      viewedSections.add(wrapper);
    } else if (clickedElement && isRapidDuplicateClick(clickedElement, action)) {
      return;
    }

    const payload = buildPayload(wrapper, target, action, clickedElement, extras);
    window.posthog.capture(EVENT_NAME, payload);

    if (clickedElement) {
      rememberModuleNavigation(payload);
    }

    if (config.debug) {
      console.debug('[Merach automatic section tracking]', payload);
    }
  }

  function requiredVisibleRatio(wrapper) {
    const height = Math.max(wrapper.getBoundingClientRect().height, 1);
    return Math.min(0.35, 240 / height);
  }

  function visibilityState(wrapper) {
    let state = visibilityStates.get(wrapper);

    if (!state) {
      state = {
        aboveThreshold: false,
        qualified: false,
        qualifyingSince: null,
        visibleSince: null,
        accumulatedMs: 0,
        lastFlushedMs: 0,
        maxVisibleRatio: 0,
        segments: 0
      };
      visibilityStates.set(wrapper, state);
      durationTrackedSections.add(wrapper);
    }

    return state;
  }

  function closeVisibleSegment(wrapper, reason) {
    const state = visibilityStates.get(wrapper);

    if (!state?.qualified || state.visibleSince === null) {
      return;
    }

    state.accumulatedMs += Math.max(0, performance.now() - state.visibleSince);
    state.visibleSince = null;
    state.segments += 1;
    flushViewDuration(wrapper, reason);
  }

  function flushViewDuration(wrapper, reason) {
    const state = visibilityStates.get(wrapper);

    if (!state?.qualified) {
      return;
    }

    const durationDeltaMs = Math.round(state.accumulatedMs - state.lastFlushedMs);

    if (durationDeltaMs <= 0) {
      return;
    }

    state.lastFlushedMs = state.accumulatedMs;
    capture(
      wrapper,
      observedTargets.get(wrapper) || wrapper,
      'view_duration',
      null,
      {
        view_duration_ms: durationDeltaMs,
        view_total_duration_ms: Math.round(state.accumulatedMs),
        view_max_visible_ratio: Number(state.maxVisibleRatio.toFixed(3)),
        view_visibility_segments: state.segments,
        view_duration_reason: reason
      }
    );
  }

  function pauseAllDurations(reason) {
    durationTrackedSections.forEach((wrapper) => {
      closeVisibleSegment(wrapper, reason);
      const state = visibilityStates.get(wrapper);

      if (state) {
        state.aboveThreshold = false;
      }
    });
  }

  function resumeDurationObservation() {
    if (!observer) {
      return;
    }

    durationTrackedSections.forEach((wrapper) => {
      if (!wrapper.isConnected) {
        durationTrackedSections.delete(wrapper);
        return;
      }

      observer.unobserve(wrapper);
      observer.observe(wrapper);
    });
  }

  function cancelVisibleTimer(wrapper) {
    const timer = visibleTimers.get(wrapper);

    if (timer) {
      window.clearTimeout(timer);
      visibleTimers.delete(wrapper);
    }
  }

  function handleIntersections(entries) {
    entries.forEach((entry) => {
      const wrapper = entry.target;
      const state = visibilityState(wrapper);
      const requiredRatio = requiredVisibleRatio(wrapper);
      const isAboveThreshold = entry.isIntersecting && entry.intersectionRatio >= requiredRatio;

      state.maxVisibleRatio = Math.max(state.maxVisibleRatio, entry.intersectionRatio);

      if (!isAboveThreshold) {
        cancelVisibleTimer(wrapper);
        state.aboveThreshold = false;
        state.qualifyingSince = null;
        closeVisibleSegment(wrapper, 'visibility_exit');
        return;
      }

      if (state.qualified) {
        state.aboveThreshold = true;

        if (state.visibleSince === null) {
          state.visibleSince = performance.now();
        }
        return;
      }

      if (visibleTimers.has(wrapper)) {
        return;
      }

      state.aboveThreshold = true;
      state.qualifyingSince = performance.now();

      const timer = window.setTimeout(() => {
        visibleTimers.delete(wrapper);
        const currentState = visibilityStates.get(wrapper);

        if (!currentState?.aboveThreshold) {
          return;
        }

        currentState.qualified = true;
        currentState.visibleSince = currentState.qualifyingSince || performance.now();
        currentState.qualifyingSince = null;

        if (!legacyOwnedSections.has(wrapper)) {
          capture(
            wrapper,
            observedTargets.get(wrapper) || wrapper,
            'view',
            null,
            {
              view_qualification_ms: VIEW_DELAY_MS,
              view_visible_ratio: Number(currentState.maxVisibleRatio.toFixed(3))
            }
          );
        }
      }, VIEW_DELAY_MS);

      visibleTimers.set(wrapper, timer);
    });
  }

  function registerSection(wrapper) {
    if (!(wrapper instanceof HTMLElement) || registeredSections.has(wrapper)) {
      return;
    }

    registeredSections.add(wrapper);

    const type = sectionType(wrapper);
    const marker = markerFor(wrapper);

    if (isExcluded(wrapper, type)) {
      return;
    }

    if (isLegacyOwned(wrapper, type, marker)) {
      legacyOwnedSections.add(wrapper);
    }

    /*
     * data-mrk-track-config is retained only as optional metadata. Its old
     * per-instance enabled flag no longer gates automatic tracking.
     */
    observedTargets.set(wrapper, marker || wrapper);

    if (observer) {
      observer.observe(wrapper);
    } else {
      const state = visibilityState(wrapper);
      state.qualified = true;
      state.aboveThreshold = true;
      state.visibleSince = performance.now();
      state.maxVisibleRatio = 1;

      if (!legacyOwnedSections.has(wrapper)) {
        capture(wrapper, marker || wrapper, 'view', null, {
          view_qualification_ms: 0,
          view_visible_ratio: 1
        });
      }
    }
  }

  function registerAllSections(root = document) {
    if (root instanceof HTMLElement && root.matches(SECTION_SELECTOR)) {
      registerSection(root);
    }

    root.querySelectorAll?.(SECTION_SELECTOR).forEach(registerSection);
  }

  function handleClick(event) {
    if (!config.trackClicks) {
      return;
    }

    const clickedElement = event.target.closest?.(INTERACTIVE_SELECTOR);

    if (!clickedElement) {
      return;
    }

    const wrapper = clickedElement.closest(SECTION_SELECTOR);

    if (!wrapper) {
      return;
    }

    const type = sectionType(wrapper);
    const marker = markerFor(wrapper);

    if (isExcluded(wrapper, type) || isLegacyOwned(wrapper, type, marker)) {
      return;
    }

    const trackedItem = clickedElement.closest('[data-mrk-track-item]') || marker || clickedElement;
    capture(
      wrapper,
      trackedItem,
      trackedItem.dataset.mrkTrackClickAction || 'click',
      clickedElement
    );
  }

  function start() {
    registerAllSections();
    // Pointer-down captures navigation before the browser unloads the page.
    // The click listener covers keyboard activation and is de-duplicated for
    // pointer interactions by isRapidDuplicateClick.
    document.addEventListener('pointerdown', handleClick, true);
    document.addEventListener('click', handleClick, true);
    document.addEventListener('shopify:section:load', (event) => registerAllSections(event.target));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        pauseAllDurations('document_hidden');
      } else {
        resumeDurationObservation();
      }
    });
    window.addEventListener('pagehide', () => pauseAllDurations('pagehide'));

    if ('MutationObserver' in window) {
      const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              registerAllSections(node);
            }
          });
        });
      });

      mutationObserver.observe(document.documentElement, {
        childList: true,
        subtree: true
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
